import { AlignmentUtil } from "../../../utils/alignment-util";
import { CssProperties } from "src/models/css-property";
import { ElementStyleService } from "../../../services/element-style.service";
import { BreadcrumbsWidgetData, IBreadcrumbsStyleData } from "src/typings/breadcrumbs-widget-data";
import { StringUtil } from "src/utils/string-util";

export class BreadcrumbsWidgetStyleRenderer {

    constructor(private readonly styleService: ElementStyleService) {
    }

    public render(element: HTMLElement, style: IBreadcrumbsStyleData): void {
        this.clearStyles(element);
        this.renderBaseStyles(element, style);
        this.renderBreadcrumbsStyles(element, style);
    }

    private renderBaseStyles(element: HTMLElement, style: IBreadcrumbsStyleData): void {
        const listCssProperties = new CssProperties();
        const id = element.id;

        if (style?.alignment) {
            const alignment = AlignmentUtil.correctAlignmentStyle(style?.alignment);
            if (alignment) {
                listCssProperties.add("justify-content", alignment);
            }
        }

        const columnGapValue = style?.spaceBetweenItems ?? BreadcrumbsWidgetData.defaultBreadcrumbsStyle.spaceBetweenItems;

        if (columnGapValue) {
            listCssProperties.add("column-gap", `${columnGapValue}px`);
        }

        this.styleService.setStyle(element, this.getListRootSelector(id), listCssProperties.data);
    }

    private renderBreadcrumbsStyles(element: HTMLElement, style: IBreadcrumbsStyleData): void {
        this.renderFontStyle(element, style);
        this.renderColors(element, style);
        this.renderIconSize(element, style);
    }

    private renderFontStyle(element: HTMLElement, style: IBreadcrumbsStyleData): void {
        const fontStyleProperties = this.getFontProperties(style);
        if (fontStyleProperties.data.length > 0) {
            this.styleService.setStyle(element, this.getNavItemSelector(element.id), fontStyleProperties.data);
        }
    }

    private getFontProperties(style: IBreadcrumbsStyleData): CssProperties {
        const cssProperties = new CssProperties();
        if (style?.font) {
            cssProperties.add("font-family", style?.font?.family ?? "");
            cssProperties.add("font-weight", style?.font?.weight ?? "");
        }

        const fontSizeProperties = this.getFontSizeProperties(style);
        fontSizeProperties.data.forEach(cssProperty => {
            if (cssProperty.value) {
                cssProperties.add(cssProperty.property, cssProperty.value);
            }
        });

        return cssProperties;
    }

    private getFontSizeProperties(style: IBreadcrumbsStyleData): CssProperties {
        const cssProperties = new CssProperties();
        if (style?.font) {
            cssProperties.add("font-size", style?.font?.size ?? "");
            if (style?.font?.lineHeight) {
                cssProperties.add("line-height", style?.font?.lineHeight ?? "");
            }
        }

        return cssProperties;
    }

    private renderColors(element: HTMLElement, style: IBreadcrumbsStyleData): void {
        this.renderFontColor(element, style?.currentPageLinkColor, this.getNavItemActiveSelector(element.id));
        this.renderFontColor(element, style?.regularLinkColorStyleData?.colorDefault, this.getNavItemNotActiveSelector(element.id));
        this.renderFontColor(element, style?.regularLinkColorStyleData?.colorHovered, this.getNavItemNameHoverNotActiveSelector(element.id));
        this.renderFontColor(element, style?.iconColor, this.getArrowIconBeforeSelector(element.id));
    }

    private renderFontColor(element: HTMLElement, color: string | undefined | null, selector: string): void {
        const fontColorProperties = this.getColorProperties(color);
        if (fontColorProperties.data.length > 0) {
            this.styleService.setStyle(element, selector, fontColorProperties.data);
        }
    }

    private getColorProperties(color?: string | null): CssProperties {
        const cssProperties = new CssProperties();
        if (color) {
            cssProperties.add("color", color);
        }

        return cssProperties;
    }

    private renderIconSize(element: HTMLElement, style: IBreadcrumbsStyleData): void {
        const sizeIconProperties = this.getSizeIconProperties(style);
        if (sizeIconProperties.data.length > 0) {
            this.styleService.setStyle(element, this.getArrowIconSelector(element.id), sizeIconProperties.data);
        }
    }

    private getSizeIconProperties(style: IBreadcrumbsStyleData): CssProperties {
        const cssProperties = new CssProperties();
        const fontSize = style?.font?.size;

        if (fontSize && !StringUtil.isEmptyOrWhiteSpace(fontSize)) {
            const value = parseFloat(fontSize);
            const unit = fontSize.replace(value.toString(), "");
            const halfSize = (value / 1.5) + unit;
            cssProperties.add("width", halfSize);
            cssProperties.add("height", halfSize);
        }

        return cssProperties;
    }

    private clearStyles(element: HTMLElement): void {
        const selectors = this.getStylesSelectors(element.id);
        selectors.forEach(selector => this.styleService.clearStyle(element, selector));
    }

    public getStylesSelectors(id: string): string[] {
        return [
            this.getListRootSelector(id),
            this.getNavItemNameSelector(id),
            this.getNavItemNameHoverSelector(id),
            this.getNavItemNameNotHoverSelector(id),
            this.getActiveNavItemNameSelector(id),
            this.getNavItemSelector(id)
        ];
    }

    private getArrowIconSelector(id: string): string {
        return `#${id} .arrow.item-icon`;
    }

    private getArrowIconBeforeSelector(id: string): string {
        return `#${id} .arrow.item-icon::before`;
    }

    private getNavItemSelector(id: string): string {
        return `#${id} li`;
    }

    private getNavItemActiveSelector(id: string): string {
        return `#${id} ul.root > li.active`;
    }

    private getNavItemNotActiveSelector(id: string): string {
        return `#${id} ul.root > li:not(.active)`;
    }

    private getNavItemNameSelector(id: string): string {
        return `#${id} .nav-item-name`;
    }

    private getListRootSelector(id: string): string {
        return `#${id} ul.root`;
    }

    private getNavItemNameHoverSelector(id: string): string {
        return `#${id} ul.root > li:hover > .nav-item-name`;
    }

    private getNavItemNameNotHoverSelector(id: string): string {
        return `#${id} ul.root > li:not(:hover) > .nav-item-name`;
    }

    private getNavItemNameHoverNotActiveSelector(id: string): string {
        return `#${id} ul.root > li:not(.active):hover > .nav-item-name`;
    }

    private getActiveNavItemNameSelector(id: string): string {
        return `#${id} ul.root > li.active:not(:hover) > .nav-item-name`;
    }

}
